/**
 * useAreaPageObservability Hook
 *
 * React hook for tracking area page load events.
 * Handles config checks, load time measurement, and one-time tracking.
 */

import { useEffect, useRef } from 'react';
import { AreaPageObservabilityService, type AreaPageType } from 'observability/services/area-page-observability';
import { isAreaPageObservabilityEnabled, isCriticalCity } from 'utils/observability/area-page-helpers';
import type { Tenant } from '@tenants/types';

export interface UseAreaPageObservabilityParams {
  /** City/area slug (e.g., 'toronto-on') */
  citySlug: string;

  /** Province or state (e.g., 'ON', 'BC', 'FL') */
  provinceOrState: string;

  /** Country code (e.g., 'CA', 'US') */
  country: string;

  /** Number of listings returned */
  listingCount: number;

  /** Page type: 'standard' or 'hybrid' */
  pageType: AreaPageType;

  /** Tenant configuration */
  tenant: Tenant<any, any, any, any>;

  /**
   * Optional: Wait for data to be ready before tracking.
   * Useful for hybrid pages where listings load client-side via boundary/cluster APIs.
   * @default true
   */
  isDataReady?: boolean;
}

/**
 * Hook to track area page load events
 *
 * Automatically tracks when component mounts with data.
 * Measures load time from mount to data availability.
 * Only tracks once per component lifecycle.
 *
 * For hybrid pages with client-side data loading, use `isDataReady` to delay tracking
 * until the boundary/cluster API calls complete.
 *
 * @example
 * ```tsx
 * // Standard area page (server-side data)
 * function AreaListingsPage({ listings, address, tenant }) {
 *   useAreaPageObservability({
 *     citySlug: address.citySlug,
 *     provinceOrState: address.provinceOrState,
 *     country: address.country,
 *     listingCount: listings.length,
 *     pageType: 'standard',
 *     tenant,
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 *
 * @example
 * ```tsx
 * // Hybrid page (client-side boundary API)
 * function HybridPage({ address, tenant }) {
 *   const [listingCount, setListingCount] = useState(0);
 *   const [isDataReady, setIsDataReady] = useState(false);
 *
 *   useEffect(() => {
 *     fetchBoundaryData().then(data => {
 *       setListingCount(data.totalCount);
 *       setIsDataReady(true);
 *     });
 *   }, []);
 *
 *   useAreaPageObservability({
 *     citySlug: address.citySlug,
 *     provinceOrState: address.provinceOrState,
 *     country: address.country,
 *     listingCount,
 *     pageType: 'hybrid',
 *     tenant,
 *     isDataReady, // Wait for boundary API to complete
 *   });
 *
 *   return <div>...</div>;
 * }
 * ```
 */
export function useAreaPageObservability(params: UseAreaPageObservabilityParams): void {
  const { citySlug, provinceOrState, country, listingCount, pageType, tenant, isDataReady = true } = params;

  const hasTrackedRef = useRef(false);
  const mountTimeRef = useRef<number | null>(null);

  // Capture mount time on first render
  if (mountTimeRef.current === null) {
    mountTimeRef.current = performance.now();
  }

  useEffect(() => {
    // Guard: Only track once
    if (hasTrackedRef.current) {
      return;
    }

    // Guard: Wait for data to be ready (important for hybrid pages)
    if (!isDataReady) {
      return;
    }

    // Guard: Check if observability is enabled
    if (!isAreaPageObservabilityEnabled(tenant)) {
      return;
    }

    // Guard: Ensure we have required data
    if (!citySlug || !provinceOrState || !country) {
      return;
    }

    // Calculate load time
    const loadTimeMs = Math.round(performance.now() - (mountTimeRef.current ?? 0));

    // Check if critical city
    const cityIsCritical = isCriticalCity(citySlug, tenant);

    // Track the event
    AreaPageObservabilityService.trackLoadSuccess({
      citySlug,
      provinceOrState,
      country,
      listingCount,
      isCriticalCity: cityIsCritical,
      loadTimeMs,
      pageType,
      tenantId: tenant.id,
    });

    // Mark as tracked
    hasTrackedRef.current = true;
  }, [citySlug, provinceOrState, country, listingCount, pageType, tenant, isDataReady]);
}

